import React, { useMemo, useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { MagnifyingGlass, ArrowRight } from "@phosphor-icons/react";

const TAB_KW = {
  website: "homepage landing page hero blog posts events seo public site marketing messages",
  calendar: "calendar bookings appointments schedule time off blocks google sync sessions packs credits",
  personal: "adult public learners personal accounts purchased courses grant course reset",
  users: "school users teachers school admins accounts students passwords reset",
  tokens: "seats licenses access tokens student logins qr login cards",
  library: "video videos media uploads documents files bunny stream storage",
  courses: "course builder lessons quizzes curriculum videos edit course modules",
  matrix: "grade matrix lesson matrix pacing due dates lesson plan grades",
  roster: "my classes class roster students kudos class codes",
  "other-classes": "other classes in my school colleagues",
  security: "2fa mfa two factor authentication security password protect account",
  pillars: "pillars terminology sections rename units",
  branding: "branding logo colors app name icon theme",
  translations: "french language translations fr bilingual",
  schools: "school boards districts schools independent",
  finance: "financial overview revenue payouts profit share earnings money strip",
  invoices: "money invoices quotes billing customers documents accounting",
  frontdesk: "front desk pos till in-person sales price list packs vouchers reviews",
  certificates: "certificates issued completion diploma verify",
  waivers: "waivers liability signatures signed release forms",
  renewals: "renewals expiry august 31 seats renewal radar expiring",
  reports: "board reports usage analytics pilot digest",
  messaging: "messaging sms twilio texts notifications quiet flag words safety",
  feedback: "feedback bug reports user messages replies",
  audit: "audit log history admin actions trail",
  instructors: "instructors trainers level 1 level 2 payouts attribution",
  "school-logo": "school logo upload",
};

const DEEP = [
  { label: "Bookings calendar", tab: "calendar", target: "website-card-calendar", desc: "Visual calendar — requests, blocked time, Google sync", kw: "booking bookings calendar appointments requests schedule sessions reschedule confirm" },
  { label: "Block time off", tab: "calendar", target: "website-card-calendar", desc: "Block days or hours so nobody can book them", kw: "block time off vacation holiday away blocked unavailable" },
  { label: "Google Calendar sync", tab: "calendar", target: "website-card-google-calendar-sync", desc: "Connect, reconnect or force a sync", kw: "google calendar sync gcal reconnect connect" },
  { label: "Promo codes / coupons", tab: "invoices", target: "promo-codes-card", desc: "Create & edit % or $ discount codes for public checkout", kw: "coupon coupons discount codes promo sale percent off deal" },
  { label: "Customers & invoice history", tab: "invoices", target: "customers-card", desc: "One record per customer — text/email them, see the full conversation, print invoices", kw: "customer client history print invoice contact text email message chat conversation booking" },
  { label: "Create an invoice or quote", tab: "invoices", desc: "Build, send and track invoices & quotes", kw: "new invoice quote estimate bill pdf send email" },
  { label: "Product catalog", tab: "invoices", target: "catalog-card", desc: "Reusable products & prices for quotes and invoices", kw: "products catalog prices items services sku" },
  { label: "Sales stats", tab: "invoices", target: "sales-stats-card", desc: "Sales totals and trends", kw: "sales stats totals trends" },
  { label: "Period report (taxes)", tab: "invoices", target: "period-report-card", desc: "Period totals for GST filing", kw: "gst tax report period filing accounting quarterly" },
  { label: "Tax matrix", tab: "invoices", target: "tax-matrix-card", desc: "Tax rates applied to documents", kw: "tax rates gst pst hst province" },
  { label: "Business identity", tab: "invoices", target: "business-identity-card", desc: "Business name, address & GST number printed on documents", kw: "business identity gst number letterhead address company info" },
  { label: "Online (Stripe) sales", tab: "invoices", target: "stripe-sales-card", desc: "Course sales taken online through Stripe", kw: "stripe online sales card payments" },
  { label: "Seat billing & blocks", tab: "invoices", target: "pending-billing-section", desc: "Pending school seats and purchased seat blocks", kw: "seat blocks pending billing bulk school seats" },
  { label: "Walk-in registration & waiver QR", tab: "frontdesk", target: "fd-walkin-card", desc: "QR that walks a customer through the waiver", kw: "walk in register waiver qr sign" },
  { label: "Front desk price list", tab: "frontdesk", target: "fd-price-list-card", desc: "Items shown on the poster and phone till", kw: "price list poster items front desk tiles" },
  { label: "Session packs", tab: "frontdesk", target: "fd-packs-card", desc: "Track sessions remaining on sold packs", kw: "session packs punch card check in remaining" },
  { label: "Course access codes (vouchers)", tab: "frontdesk", target: "fd-vouchers-card", desc: "Codes minted for online training sold at the desk — resend from here", kw: "voucher access codes e650 resend unlock online training" },
  { label: "Front desk transactions", tab: "frontdesk", target: "fd-transactions-card", desc: "Every charge incl. Tap to Pay — receipts, log as pack / course", kw: "transactions tap to pay receipts qr charges log as pack terminal" },
  { label: "Phone till (charge screen)", href: "/frontdesk", desc: "Full-screen point of sale for your phone", kw: "till pos point of sale charge cart phone front desk mode" },
  { label: "Printable price poster", href: "/poster", desc: "Wall poster with QR codes for every item", kw: "poster print qr wall prices" },
  { label: "Google review poster", href: "/review-poster", desc: "Printable poster asking for a Google review", kw: "google review stars poster rating" },
];

export function AdminSearchBar({ tabs, selectTab }) {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [focus, setFocus] = useState(false);
  const boxRef = useRef(null);

  const tabLabel = useMemo(() => Object.fromEntries(tabs.map((t) => [t.id, t.label])), [tabs]);
  const index = useMemo(() => {
    const ids = new Set(tabs.map((t) => t.id));
    const tabEntries = tabs.map((t) => ({
      label: t.label, tab: t.id, desc: "Open this tab", kw: TAB_KW[t.id] || "",
    }));
    const deep = DEEP.filter((d) => (d.tab ? ids.has(d.tab) : ids.has("frontdesk")));
    return [...deep, ...tabEntries];
  }, [tabs]);

  const results = useMemo(() => {
    const words = q.toLowerCase().trim().split(/\s+/).filter(Boolean);
    if (!words.length) return [];
    const scored = [];
    for (const e of index) {
      const label = e.label.toLowerCase();
      const hay = `${label} ${e.kw} ${(e.desc || "").toLowerCase()}`;
      if (!words.every((w) => hay.includes(w))) continue;
      let score = 0;
      if (label.startsWith(words[0])) score += 3;
      if (words.every((w) => label.includes(w))) score += 2;
      if (e.target || e.href) score += 1;
      scored.push([score, e]);
    }
    return scored.sort((a, b) => b[0] - a[0]).map(([, e]) => e).slice(0, 8);
  }, [q, index]);

  useEffect(() => {
    const onDoc = (ev) => { if (boxRef.current && !boxRef.current.contains(ev.target)) setFocus(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const flash = (tid, tries = 0) => {
    const el = document.querySelector(`[data-testid="${tid}"]`) || document.querySelector(`[data-testid^="${tid}"]`);
    if (!el) { if (tries < 12) setTimeout(() => flash(tid, tries + 1), 300); return; }
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.style.transition = "box-shadow 0.4s";
    el.style.boxShadow = "0 0 0 3px #CCFF00, 0 0 22px rgba(204,255,0,0.55)";
    setTimeout(() => { el.style.boxShadow = ""; }, 2600);
  };

  const go = (e) => {
    setQ(""); setFocus(false);
    if (e.href) { nav(e.href); return; }
    selectTab(e.tab);
    if (e.target) setTimeout(() => flash(e.target), 350);
    else window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div ref={boxRef} className="relative mt-5 max-w-xl" data-testid="admin-search">
      <div className="flex items-center gap-2 bg-white border border-[#C4C8CE] rounded-full px-4 py-2.5 focus-within:border-[#84cc16]">
        <MagnifyingGlass size={18} color="#52525B" className="shrink-0" />
        <input
          className="flex-1 bg-transparent border-0 outline-none text-sm min-w-0"
          placeholder='Find anything… try "coupons", "waivers" or "resend access code"'
          value={q}
          onChange={(ev) => setQ(ev.target.value)}
          onFocus={() => setFocus(true)}
          onKeyDown={(ev) => {
            if (ev.key === "Enter" && results[0]) go(results[0]);
            if (ev.key === "Escape") { setQ(""); setFocus(false); }
          }}
          data-testid="admin-search-input"
        />
      </div>
      {focus && q.trim().length >= 2 && (
        <div className="absolute left-0 right-0 top-full mt-1.5 z-40 bg-white border border-[#C4C8CE] rounded-2xl shadow-xl overflow-hidden" data-testid="admin-search-results">
          {results.length === 0 ? (
            <div className="px-4 py-3 text-sm text-[#000000] italic">No matches — try another word (e.g. "coupons", "packs", "waiver")</div>
          ) : results.map((e, i) => (
            <button
              key={`${e.label}-${i}`}
              type="button"
              onClick={() => go(e)}
              className="w-full text-left px-4 py-2.5 bg-transparent border-0 cursor-pointer hover:bg-[#F7FBEF] flex items-center gap-3 border-b border-[#ECEDEF] last:border-b-0"
              data-testid={`admin-search-result-${i}`}
            >
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold">{e.label}</div>
                <div className="text-xs text-[#000000] truncate">
                  {e.href ? `Page · ${e.href}` : e.target ? `${tabLabel[e.tab] || e.tab} tab` : "Tab"}{e.desc ? ` — ${e.desc}` : ""}
                </div>
              </div>
              <ArrowRight size={15} color="#3F6212" className="shrink-0" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
